import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';

import notie from 'notie';

import portfolioData from './portfolioData.json';
import './Portfolio.css';

function Portfolio() {
    const dialogRef = useRef(null);
    const { projectId } = useParams();
    const navigate = useNavigate();
    const selectedProject = portfolioData.find((project) => project.id === projectId);
    const featuredProject = portfolioData.find((project) => project.id === 'lancely');
    const featuredProjects = portfolioData.filter((project) => project.featured && project.id !== 'lancely');

    useEffect(() => {
        if (projectId && !selectedProject) {
            navigate('/portfolio', { replace: true });
        }
    }, [navigate, projectId, selectedProject]);

    useEffect(() => {
        if (!selectedProject) {
            return undefined;
        }

        const content = document.getElementById('content');

        document.documentElement.classList.add('project-modal-open');
        document.body.classList.add('project-modal-open');
        content?.classList.add('project-modal-open');

        dialogRef.current?.focus();

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                navigate('/portfolio', { state: { preserveScroll: true } });
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.documentElement.classList.remove('project-modal-open');
            document.body.classList.remove('project-modal-open');
            content?.classList.remove('project-modal-open');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [navigate, selectedProject]);

    const comingSoon = () => {
        notie.alert({
            type: "success",
            text: "Coming Soon!",
            stay: false,
            time: 5,
            position: "top"
        });
    }

    const closeProject = () => {
        navigate('/portfolio', { state: { preserveScroll: true } });
    };

    const getProjectPath = (project) => project.caseStudyPath || `/portfolio/${project.id}`;
    const getProjectLinkState = (project) => project.caseStudyPath ? undefined : { preserveScroll: true };

    return (
        <>
            <div className="container portfolio-page">
                <section className="portfolio-intro">
                    <p className="eyebrow">Selected work</p>
                    <h1 className="display-4 text-bold text-indigo">Portfolio</h1>
                    <p className="lead">
                        A selection of product, app, open-source, and web work across personal projects, client-style builds, and early product concepts.
                    </p>
                    <div className="portfolio-actions">
                        <Link to="/services" className="btn btn-indigo">See Project Packages</Link>
                        <Link to="/contact" className="btn btn-link">Request a Quote</Link>
                    </div>
                </section>

                {featuredProject && (
                    <section aria-labelledby="featured-project-heading">
                        <h2 id="featured-project-heading" className="display-6 text-indigo">Featured Case Study</h2>
                        <Link className="featured-project-card" to={featuredProject.caseStudyPath}>
                            <div className="featured-project-copy">
                                <p className="eyebrow">Product case study</p>
                                <h3>{featuredProject.name}</h3>
                                <p>{featuredProject.description}</p>
                            </div>
                            <img src={`/assets/imgs/portfolio/${featuredProject.img}`} alt={`${featuredProject.name} dashboard preview`} />
                        </Link>
                    </section>
                )}

                <section className="featured-work-section" aria-labelledby="featured-work-heading">
                    <div className="section-heading-row">
                        <div>
                            <p className="eyebrow">Featured work</p>
                            <h2 id="featured-work-heading" className="display-6 text-indigo">Product and Web Projects</h2>
                        </div>
                        <p>
                            Projects that show product thinking, UI implementation, open-source work, and operational tooling.
                        </p>
                    </div>
                    <div className="featured-work-grid">
                        {featuredProjects.map((project) => (
                            <article className="featured-work-card" key={project.id}>
                                <div>
                                    <h3>{project.name}</h3>
                                    <p>{project.description}</p>
                                </div>
                                <Link className="text-link" state={getProjectLinkState(project)} to={getProjectPath(project)}>
                                    View project
                                </Link>
                            </article>
                        ))}
                    </div>
                </section>

                <section aria-labelledby="project-gallery-heading">
                    <div className="section-heading-row">
                        <div>
                            <p className="eyebrow">More work</p>
                            <h2 id="project-gallery-heading" className="display-6 text-indigo">Project Gallery</h2>
                        </div>
                        <p>
                            Browse the broader archive of apps, websites, open-source experiments, and product concepts.
                        </p>
                    </div>
                    <div id="portfolio" className="project-grid">
                        {portfolioData.map((project) => (
                            <article className="project-card" key={project.id}>
                                <Link className="project-card-button" state={getProjectLinkState(project)} to={getProjectPath(project)}>
                                    <span className="project-card-image">
                                        <img src={`/assets/imgs/portfolio/${project.img}`} alt={`${project.name} preview`} />
                                    </span>
                                    <span className="project-card-title">{project.name}</span>
                                    <span className="project-card-summary">{project.description}</span>
                                </Link>
                                {project.tags?.length > 0 && (
                                    <ul className="project-tags" aria-label={`${project.name} categories`}>
                                        {project.tags.map((tag) => (
                                            <li key={tag}>{tag}</li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            </div>

            {selectedProject && createPortal((
                <div className="project-modal-backdrop" role="presentation" onMouseDown={closeProject}>
                    <section
                        aria-describedby="project-description"
                        aria-labelledby="project-title"
                        aria-modal="true"
                        className="project-modal"
                        onMouseDown={(event) => event.stopPropagation()}
                        ref={dialogRef}
                        role="dialog"
                        tabIndex={-1}
                    >
                        <button className="project-modal-close" type="button" onClick={closeProject} aria-label="Close project details">
                            &times;
                        </button>
                        <img src={`/assets/imgs/portfolio/${selectedProject.img}`} alt={`${selectedProject.name} preview`} />
                        <h2 id="project-title">{selectedProject.name}</h2>
                        <p id="project-description" className="lead">{selectedProject.description}</p>
                        {selectedProject.link && (
                            <a className="btn btn-indigo width-75" href={selectedProject.link} target="_blank" rel="noreferrer">
                                View Project
                            </a>
                        )}
                        {selectedProject.appsComingSoon && (
                            <div className="app-links">
                                <p>Mobile app links are coming soon.</p>
                                <button type="button" onClick={comingSoon}>
                                    <img src="/assets/imgs/companies/google-play.png" alt="Google Play" />
                                    <img src="/assets/imgs/companies/apple-store.png" alt="Apple App Store" />
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            ), document.body)}
        </>
    )
}

export default Portfolio;
